const INITIAL_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;
// gtw sends a heartbeat every 20s; silence beyond this means the connection is dead (e.g. laptop sleep)
const IDLE_TIMEOUT_MS = 45_000;

/**
 * Opens a Server-Sent Events stream with fetch, since EventSource cannot send an Authorization header.
 * Reconnects with jittered exponential backoff, fetching a fresh token on every attempt.
 *
 * @param {object} options
 * @param {string} options.url
 * @param {() => string} options.getToken
 * @param {(type: string, data: any) => void} options.onEvent
 * @param {() => void} options.onUnauthorized
 * @returns {() => void} closes the stream
 */
export const openSseStream = ({ url, getToken, onEvent, onUnauthorized }) => {
  const controller = new AbortController();

  const run = async () => {
    let attempt = 0;
    while (!controller.signal.aborted) {
      const attemptController = new AbortController();
      const abortAttempt = () => attemptController.abort();
      controller.signal.addEventListener('abort', abortAttempt);

      try {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${getToken()}`, Accept: 'text/event-stream' },
          cache: 'no-store',
          signal: attemptController.signal,
        });

        if (res.status === 401) {
          onUnauthorized();
        } else if (res.ok && res.body) {
          const receivedEvents = await readEvents(res.body, attemptController, onEvent);
          if (receivedEvents) {
            attempt = 0;
          }
        } else {
          console.warn(`[SSE] ${url} responded ${res.status}`);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.warn('[SSE] connection lost', error);
        }
      } finally {
        controller.signal.removeEventListener('abort', abortAttempt);
      }

      if (!controller.signal.aborted) {
        await sleep(retryDelay(attempt++), controller.signal);
      }
    }
  };

  run();
  return () => controller.abort();
};

/**
 * @returns {Promise<boolean>} whether any event was received before the stream ended
 */
const readEvents = async (body, attemptController, onEvent) => {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let receivedEvents = false;
  let idleTimer;
  const resetIdleTimer = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => attemptController.abort(), IDLE_TIMEOUT_MS);
  };

  try {
    resetIdleTimer();
    while (true) {
      const { value, done } = await reader.read();
      if (done) return receivedEvents;
      resetIdleTimer();

      buffer += decoder.decode(value, { stream: true }).replace(/\r\n?/g, '\n');
      let boundary;
      while ((boundary = buffer.indexOf('\n\n')) >= 0) {
        dispatchBlock(buffer.slice(0, boundary), onEvent);
        buffer = buffer.slice(boundary + 2);
        receivedEvents = true;
      }
    }
  } catch (error) {
    // An established stream that drops (idle timeout, network) should retry quickly
    if (receivedEvents) return true;
    throw error;
  } finally {
    clearTimeout(idleTimer);
  }
};

const dispatchBlock = (block, onEvent) => {
  let type = 'message';
  const dataLines = [];
  for (const line of block.split('\n')) {
    if (line === '' || line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    if (field === 'event') type = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (dataLines.length === 0) return;

  try {
    onEvent(type, JSON.parse(dataLines.join('\n')));
  } catch (error) {
    console.error(`[SSE] unable to handle ${type} event`, error);
  }
};

const retryDelay = (attempt) => {
  const delay = Math.min(MAX_RETRY_MS, INITIAL_RETRY_MS * 2 ** attempt);
  return delay / 2 + Math.random() * (delay / 2);
};

const sleep = (ms, signal) =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
